import { AgendaItemWithTimes } from '@livekatsomo/models';
import { useDateFns } from '@livekatsomo/web/hooks';
import { LiveAvatar } from '@livekatsomo/web/ui-components/avatar';
import FreeBreakfast from '@mui/icons-material/FreeBreakfast';
import AvatarGroup from '@mui/material/AvatarGroup';
import Box, { BoxProps } from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { addMinutes } from 'date-fns';
import { LinkProps } from 'next/link';

/**
 * Props for the AgendaListItem component
 */
export interface AgendaListItemProps {
  /** The agenda item to display */
  agendaItem: AgendaItemWithTimes;
  /** Whether the item is currently active */
  active?: boolean;
  /** The href for the link to the item */
  href?: LinkProps['href'];
}

/**
 * A component that displays an agenda item
 */
export function AgendaListItem<C extends React.ElementType>({
  agendaItem,
  // active = false,
  sx,
  href,
  ...props
}: BoxProps<C, AgendaListItemProps>) {
  const { format } = useDateFns();
  const {
    type,
    title,
    startTime,
    duration,
    description,
    speakers,
    showSpeakers,
    includeSpeakerDescription,
  } = agendaItem;

  return (
    <Box
      key={agendaItem.id}
      // className={active ? 'Mui-selected' : ''}
      sx={{
        alignItems: 'center',
        display: 'flex',
        gap: 0,
        py: 2,
        flexWrap: 'wrap',
        opacity: agendaItem.disabled ? 0.5 : 1,
        ...sx,
      }}
      {...props}
    >
      {agendaItem.showTime || agendaItem.showTime === undefined ? (
        <Typography
          data-chromatic="ignore"
          sx={{
            marginRight: 2,
          }}
        >
          {format(startTime, 'HH:mm')} -{' '}
          {format(addMinutes(startTime, duration), 'HH:mm')}
        </Typography>
      ) : null}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minWidth: 'min(100%,350px)',
          marginRight: 2,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {title}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {description}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 'bold',
          }}
        >
          {type === 'talk' &&
            showSpeakers &&
            speakers
              ?.map((speaker, index) =>
                speakers && includeSpeakerDescription && speaker.description
                  ? `${speaker.name}, ${speaker.description}`
                  : `${speaker.name}`,
              )
              .join(includeSpeakerDescription ? '| ' : ', ')}
        </Typography>
      </Box>
      {type === 'talk' && speakers?.length ? (
        <AvatarGroup max={4} color="primary">
          {speakers?.map((speaker) => (
            <LiveAvatar
              key={speaker.id}
              name={speaker.name}
              asset={speaker.image}
            />
          ))}
        </AvatarGroup>
      ) : null}
      {type === 'break' ? <FreeBreakfast /> : null}
    </Box>
  );
}
